import {
  useAddSurveyMutation,
  useUpdateSurveyMutation,
  useGetSurveyByIdLazyQuery,
  useAddQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useAddOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useAddSurveyQuestionMutation,
  useDeleteSurveyQuestionMutation,
  useGetOrganizationByIdLazyQuery,
} from "graphql/__generated__/hooks";
import { useQuery } from "@apollo/client";
import { GET_SURVEYS_WITH_NO_ORG, GET_SURVEYS, GET_SURVEYS_BY_ORG_ID } from "graphql/survey/survey.query";
import { Survey } from "types/survey";
import { useNotifications } from "hooks/useNotifications";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Question } from "types/question";
import { Option } from "types/option";
import {
  Messages,
  NotificationSeverity,
} from "helpers/constants";
import {
  getNewEntitiesIds,
  getRemovedEntitiesRelationIds,
  getEditedEntitiesList,
} from "utils/FilterEntities";

export const useSurveyCreateAndEdit = (edit: boolean = false) => {
  const { surveyId, organizationId } = useParams();
  const isOrgView = useMemo(() => !!organizationId, [organizationId]);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  
  // Configure refetch queries based on organization context
  const refetchQueriesConfig = useMemo(() => {
    if (organizationId) {
      return [
        {
          query: GET_SURVEYS_BY_ORG_ID,
          variables: { orgId: organizationId },
        },
      ];
    }
    return [{ query: GET_SURVEYS }];
  }, [organizationId]);
  
  const [addSurvey] = useAddSurveyMutation({
    refetchQueries: refetchQueriesConfig,
  });
  const [updateSurvey] = useUpdateSurveyMutation();
  const [getSurveyById, { data: dataSurvey }] = useGetSurveyByIdLazyQuery();
  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();
  const [addQuestion] = useAddQuestionMutation();
  const [updateQuestion] = useUpdateQuestionMutation();
  const [deleteQuestion] = useDeleteQuestionMutation();
  const [addOption] = useAddOptionMutation();
  const [updateOption] = useUpdateOptionMutation();
  const [deleteOption] = useDeleteOptionMutation();
  const [addSurveyQuestion] = useAddSurveyQuestionMutation();
  const [deleteSurveyQuestion] = useDeleteSurveyQuestionMutation();
  const { notify } = useNotifications();

  // Fetch global surveys (with no org_id) for import
  const { data: globalSurveysData } = useQuery(GET_SURVEYS_WITH_NO_ORG);

  const globalSurveys = useMemo(() => {
    if (!globalSurveysData?.survey) return [];
    return globalSurveysData.survey.map((s: any) => ({
      survey_id: s.survey_id,
      name: s.name || "",
      org_id: s.org_id || undefined,
      questions: s.questions?.map((sq: any) => ({
        survey_question_id: sq.survey_question_id,
        question: {
          question_id: sq.question?.question_id,
          title: sq.question?.title || "",
          type: sq.question?.type || "open_ended",
          body: sq.question?.body || "",
          order: sq.question?.order || 0,
          options: (sq.question?.options || []).map((opt: any) => ({
            option_id: opt.option_id,
            text: opt.text || "",
            order: opt.order || 0,
            question_id: sq.question?.question_id || "",
          })),
        },
      })) || [],
    })) as Survey[];
  }, [globalSurveysData]);

  const organizationName = useMemo(() => {
    return organizationData?.organization_by_pk?.name || "";
  }, [organizationData]);

  useEffect(() => {
    if (organizationId) {
      getOrganizationById({
        variables: { organizationId: organizationId },
      });
    }
  }, [organizationId, getOrganizationById]);

  useEffect(() => {
    if (edit && surveyId) {
      getSurveyById({
        variables: { surveyId },
      });
    }
  }, [edit, surveyId, getSurveyById]);

  useEffect(() => {
    if (dataSurvey?.survey_by_pk && edit) {
      const surveyQuestions = dataSurvey.survey_by_pk.questions || [];
      const questionsData: Question[] = surveyQuestions.map((sq: any) => ({
        question_id: sq.question?.question_id,
        title: sq.question?.title || "",
        type: sq.question?.type || "open_ended",
        body: sq.question?.body || "",
        order: sq.question?.order || 0,
        options: (sq.question?.options || []).map((opt: any) => ({
          option_id: opt.option_id,
          text: opt.text || "",
          order: opt.order || 0,
          question_id: sq.question?.question_id || "",
        })),
      }));
      setQuestions(questionsData);
    }
  }, [dataSurvey, edit]);

  const createQuestions = async (surveyId: string) => {
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const { data: questionData } = await addQuestion({
        variables: {
          title: question.title,
          type: question.type,
          body: question.body,
          order: question.order || i + 1,
        },
      });

      const createdQuestionId = questionData?.insert_question?.returning?.[0]?.question_id;
      if (!createdQuestionId) continue;

      // Link question to survey
      await addSurveyQuestion({
        variables: {
          questionId: createdQuestionId,
          surveyId: surveyId,
        },
      });

      // Add options if multiple choice, single choice, or likert
      if ((question.type === "multiple_choice" || question.type === "single_choice" || question.type === "likert") && question.options) {
        for (let j = 0; j < question.options.length; j++) {
          const option = question.options[j];
          await addOption({
            variables: {
              questionId: createdQuestionId,
              text: option.text,
              order: option.order || j + 1,
            },
          });
        }
      }
    }
  };

  const editQuestions = async (surveyId: string) => {
    const existingQuestions = (dataSurvey?.survey_by_pk?.questions || []).map(
      (sq: any) => sq.question,
    );

    const newQuestions = getNewEntitiesIds(
      existingQuestions,
      questions,
      "question_id",
    );

    const removedQuestions = getRemovedEntitiesRelationIds(
      existingQuestions,
      questions,
      [],
      "question_id",
      null,
      null,
    );

    // Create new questions
    for (const question of questions) {
      // A question is new if it doesn't have an ID or its ID is in the newQuestions list
      const isNewQuestion = !question.question_id || newQuestions.includes(question.question_id);
      
      if (isNewQuestion) {
        const { data: questionData } = await addQuestion({
          variables: {
            title: question.title,
            type: question.type,
            body: question.body,
            order: question.order || 0,
          },
        });

        const createdQuestionId = questionData?.insert_question?.returning?.[0]?.question_id;
        if (!createdQuestionId) continue;

        await addSurveyQuestion({
          variables: {
            questionId: createdQuestionId,
            surveyId: surveyId,
          },
        });

        if ((question.type === "multiple_choice" || question.type === "single_choice" || question.type === "likert") && question.options) {
          for (let j = 0; j < question.options.length; j++) {
            const option = question.options[j];
            await addOption({
              variables: {
                questionId: createdQuestionId,
                text: option.text,
                order: option.order || j + 1,
              },
            });
          }
        }
      }
    }

    // Update existing questions
    const updatedQuestions = getEditedEntitiesList(
      existingQuestions,
      questions,
      "question_id",
    );

    for (const question of updatedQuestions) {
      await updateQuestion({
        variables: {
          id: question.question_id!,
          question: {
            title: question.title,
            type: question.type,
            body: question.body,
            order: question.order,
          },
        },
      });

      // Handle options
      const existingOptions = (existingQuestions.find(
        (q: any) => q.question_id === question.question_id,
      )?.options || []) as Option[];

      const newOptions = getNewEntitiesIds(
        existingOptions,
        question.options || [],
        "option_id",
      );

      const removedOptions = getRemovedEntitiesRelationIds(
        existingOptions,
        question.options || [],
        [],
        "option_id",
        null,
        null,
      );

      // Add new options
      for (const option of question.options || []) {
        // An option is new if it doesn't have an ID or its ID is in the newOptions list
        const isNewOption = !option.option_id || newOptions.includes(option.option_id);
        
        if (isNewOption) {
          await addOption({
            variables: {
              questionId: question.question_id!,
              text: option.text,
              order: option.order || 0,
            },
          });
        }
      }

      // Update existing options
      const updatedOptions = getEditedEntitiesList(
        existingOptions,
        question.options || [],
        "option_id",
      );

      for (const option of updatedOptions) {
        await updateOption({
          variables: {
            id: option.option_id!,
            option: {
              text: option.text,
              order: option.order,
            },
          },
        });
      }

      // Delete removed options
      for (const optionId of removedOptions) {
        await deleteOption({
          variables: { id: optionId },
        });
      }
    }

    // Delete removed questions
    for (const questionId of removedQuestions) {
      const surveyQuestion = (dataSurvey?.survey_by_pk?.questions || []).find(
        (sq: any) => sq.question?.question_id === questionId,
      );
      if (surveyQuestion?.survey_question_id) {
        await deleteSurveyQuestion({
          variables: { id: surveyQuestion.survey_question_id },
        });
      }
      await deleteQuestion({
        variables: { id: questionId },
      });
    }
  };

  const handleCreateSurvey = async (values: { name: string }) => {
    try {
      const { data } = await addSurvey({
        variables: {
          name: values.name,
          orgId: organizationId || null,
        },
      });

      const createdSurveyId = data?.insert_survey?.returning?.[0]?.survey_id;
      if (!createdSurveyId) {
        throw new Error("Failed to create survey");
      }

      await createQuestions(createdSurveyId);

      notify({
        message: Messages.successRequirementCreated,
        severity: NotificationSeverity.success,
      });

      navigate(
        organizationId
          ? `/organization/${organizationId}/surveys`
          : "/surveys",
      );
    } catch (error: any) {
      notify({
        message: error.message || "Error creating survey",
        severity: NotificationSeverity.error,
      });
    }
  };

  const handleEditSurvey = async (values: { name: string }) => {
    if (!surveyId) return;

    try {
      await updateSurvey({
        variables: {
          id: surveyId,
          survey: {
            name: values.name,
          },
        },
      });

      await editQuestions(surveyId);

      notify({
        message: Messages.successRequirementUpdated,
        severity: NotificationSeverity.success,
      });

      navigate(
        organizationId
          ? `/organization/${organizationId}/surveys`
          : "/surveys",
      );
    } catch (error: any) {
      notify({
        message: error.message || "Error updating survey",
        severity: NotificationSeverity.error,
      });
    }
  };

  return {
    questions,
    setQuestions,
    isOrgView,
    handleCreateSurvey,
    handleEditSurvey,
    dataSurvey,
    organizationName,
    globalSurveys,
  };
};
